import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import SceneTimer from '../shared/SceneTimer';
import styles from './ChaosSimulatorEngine.module.css';

const ZONES = [
  { id: 'A', x: 80, y: 100, width: 180, height: 140, label: 'Zone A', description: 'Front-left cluster' },
  { id: 'B', x: 340, y: 180, width: 140, height: 100, label: 'Zone B', description: 'Back-right desk' },
  { id: 'C', x: 340, y: 80, width: 140, height: 90, label: 'Zone C', description: 'Centre group' },
  { id: 'D', x: 80, y: 260, width: 180, height: 60, label: 'Zone D', description: 'Window-side desk' }
];

// Student positions within zones
const STUDENTS = [
  // Zone A - front-left cluster (quiet working)
  { id: 's1', zone: 'A', cx: 120, cy: 140 },
  { id: 's2', zone: 'A', cx: 160, cy: 160 },
  { id: 's3', zone: 'A', cx: 200, cy: 130 },
  // Zone B - back-right (disruptive, talking)
  { id: 's4', zone: 'B', cx: 380, cy: 210, state: 'talking' },
  { id: 's5', zone: 'B', cx: 420, cy: 230, state: 'listening' },
  // Zone C - centre (working normally)
  { id: 's6', zone: 'C', cx: 370, cy: 110 },
  { id: 's7', zone: 'C', cx: 410, cy: 130 },
  // Zone D - window (head down)
  { id: 's8', zone: 'D', cx: 140, cy: 280, state: 'sleeping' }
];

const ChaosSimulatorEngine = ({ gamifiedQuestion, onAnswer, accent = '#457b9d' }) => {
  const [selected, setSelected] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [ripples, setRipples] = useState([]);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 4) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === selected);

  useEffect(() => {
    const timer = setTimeout(() => setSceneReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleZoneClick = (zoneId) => {
    if (!sceneReady || selected) return;

    // Add ripple effect
    const zone = ZONES.find(z => z.id === zoneId);
    if (zone) {
      setRipples(prev => [...prev, { id: Date.now(), x: zone.x + zone.width/2, y: zone.y + zone.height/2 }]);
    }

    // Find the option that corresponds to this zone
    const zoneIndex = ZONES.findIndex(z => z.id === zoneId);
    const option = options[zoneIndex];

    if (option) {
      setSelected(option.label);

      // Minimum 600ms before outcome
      setTimeout(() => {
        setShowOutcome(true);
      }, 600);
    }
  };

  const completeIfReady = () => {
    if (selected && !showOutcome) {
      setShowOutcome(true);
    }
  };

  const getStudentAnimation = (student) => {
    if (student.state === 'talking') {
      return { scale: [1, 1.05, 1], rotate: [-2, 2, -2] };
    } else if (student.state === 'sleeping') {
      return { opacity: [0.5, 0.8, 0.5] };
    }
    return {};
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.svg
          viewBox="0 0 520 340"
          className={styles.sceneSvg}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Classroom background */}
          <rect x="0" y="0" width="520" height="340" fill="#fafafa" rx="12" />

          {/* Teacher desk at top-centre */}
          <motion.g
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <rect x="220" y="20" width="80" height="40" rx="4" className={styles.teacherDesk} />
            <text x="260" y="45" textAnchor="middle" className={styles.teacherLabel}>T</text>
          </motion.g>

          {/* Zones */}
          {ZONES.map((zone, index) => (
            <motion.g
              key={zone.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              onClick={() => handleZoneClick(zone.id)}
              onMouseEnter={() => setHoveredZone(zone.id)}
              onMouseLeave={() => setHoveredZone(null)}
              className={styles.zoneGroup}
            >
              {/* Zone highlight */}
              {(hoveredZone === zone.id || selected === options[index]?.label) && (
                <motion.rect
                  x={zone.x - 8}
                  y={zone.y - 8}
                  width={zone.width + 16}
                  height={zone.height + 16}
                  rx="8"
                  className={styles.zoneHighlight}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}

              {/* Zone background */}
              <rect
                x={zone.x}
                y={zone.y}
                width={zone.width}
                height={zone.height}
                rx="6"
                className={`${styles.zone} ${selected === options[index]?.label ? styles.zoneSelected : ''}`}
              />

              {/* Zone label */}
              <text x={zone.x + zone.width/2} y={zone.y + 20} textAnchor="middle" className={styles.zoneLabel}>
                {zone.label}
              </text>

              {/* Students in this zone */}
              {STUDENTS.filter(s => s.zone === zone.id).map((student, sIndex) => (
                <motion.g
                  key={student.id}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    ...getStudentAnimation(student)
                  }}
                  transition={{
                    delay: 0.4 + index * 0.1 + sIndex * 0.05,
                    repeat: student.state ? Infinity : 0,
                    duration: student.state === 'talking' ? 0.8 : 2
                  }}
                >
                  {/* Student head */}
                  <circle cx={student.cx} cy={student.cy} r="8" className={styles.studentHead} />
                  {/* Student body */}
                  <line x1={student.cx} y1={student.cy + 8} x2={student.cx} y2={student.cy + 22} className={styles.studentBody} />
                </motion.g>
              ))}

              {/* Speech waves for Zone B (disruptive student) */}
              {zone.id === 'B' && (
                <motion.g>
                  {[0, 1, 2].map((i) => (
                    <motion.path
                      key={i}
                      d={`M ${zone.x + zone.width + 10} ${zone.y + 30 + i * 15} L ${zone.x + zone.width + 25} ${zone.y + 25 + i * 15} L ${zone.x + zone.width + 40} ${zone.y + 30 + i * 15}`}
                      className={styles.speechWave}
                      animate={{ opacity: [0.3, 1, 0.3], x: [0, 5, 0] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.2,
                        delay: i * 0.2
                      }}
                    />
                  ))}
                </motion.g>
              )}
            </motion.g>
          ))}

          {/* Click ripples */}
          <AnimatePresence>
            {ripples.map((ripple) => (
              <motion.g key={ripple.id}>
                {[0, 1, 2].map((i) => (
                  <motion.circle
                    cx={ripple.x}
                    cy={ripple.y}
                    r={20 + i * 15}
                    className={styles.ripple}
                    initial={{ opacity: 0.6, scale: 0 }}
                    animate={{ opacity: 0, scale: 2 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    onAnimationComplete={() => {
                      setRipples(prev => prev.filter(r => r.id !== ripple.id));
                    }}
                  />
                ))}
              </motion.g>
            ))}
          </AnimatePresence>

          {/* Wrong zone indicator */}
          <AnimatePresence>
            {selected && selectedOption && selected !== 'B' && (
              <motion.g
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: -20 }}
                exit={{ opacity: 0 }}
              >
                <text x="260" y="170" textAnchor="middle" className={styles.wrongIndicator}>✗</text>
              </motion.g>
            )}
          </AnimatePresence>
        </motion.svg>

        {/* Timer overlay */}
        <div className={styles.timerWrap}>
          <SceneTimer duration={9000} onExpire={completeIfReady} paused={showOutcome} />
        </div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={styles.optionsArea}>
        {options.map((option, index) => {
          const zone = ZONES[index];
          return (
            <motion.button
              key={option.label}
              type="button"
              className={`${styles.choiceBtn} ${selected === option.label ? styles.selected : ''} ${selected && selected !== option.label ? styles.dimmed : ''}`}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 + index * 0.1, type: 'spring', stiffness: 100 }}
              whileHover={!selected ? { y: -2, borderColor: accent } : {}}
              whileTap={!selected ? { scale: 0.98 } : {}}
              onClick={() => handleZoneClick(zone?.id)}
              disabled={!!selected}
            >
              <span className={styles.choiceLabel}>{option.label}</span>
              <span className={styles.choiceZone}>{zone?.label}</span>
              <span className={styles.choiceText}>{option.actionText || option.text}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'B' ? 'success' : 'warning'}
        message={selectedOption?.consequence || 'Classroom scan complete.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default ChaosSimulatorEngine;
